/**
 * AddonsPricing — à la carte add-on pricing component
 *
 * Displays the new add-on pricing model with:
 * - Monthly/Annual toggle (annual ~17% off, "2 meses grátis")
 * - USD / BRL currency toggle (auto-detected, manual override persisted in localStorage)
 * - Free base card
 * - Stackable add-on cards (IA Studio, Craft, Outlining)
 * - Bundle "Tudo" highlight card
 * - Cover credits top-up block
 *
 * Checkout is not yet wired — CTA calls onSubscribe(addonId, cycle) stub
 * that shows a "coming soon" toast. Plug real checkout there when ready.
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import {
  Check,
  Sparkles,
  Package,
  ImagePlus,
  Plus,
  Zap,
  Shield,
  RotateCcw,
  PenLine,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { notify } from '../lib/toast';
import { useCurrency } from '../lib/pricing';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth-store';
import { AmbientParticles } from './ui/ambient-particles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AddonId = 'free' | 'ia-studio' | 'craft' | 'outlining' | 'bundle';
export type BillingCycle = 'monthly' | 'yearly';

export interface AddonsPricingProps {
  /** 'public' = landing/pricing page; 'app' = in-app account view */
  variant?: 'public' | 'app';
  /**
   * Called when user clicks a subscribe CTA.
   * Currently shows a "coming soon" toast — swap for real checkout logic.
   */
  onSubscribe?: (addonId: AddonId, cycle: BillingCycle) => void;
  /**
   * Force the horizontal carousel at ALL breakpoints (never switch to the
   * desktop grid). Used inside the in-app Upgrade modal/drawer where a focused
   * swipeable carousel reads better than a cramped 4-column grid.
   */
  forceSlider?: boolean;
}

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

interface AddonFeature {
  text: string;
  key: string;
  highlight?: boolean;
}

interface Addon {
  id: AddonId;
  /** Lucide icon component — only used for the free base plan */
  Icon?: React.ElementType;
  /** Slug for the illustrated webp icon (non-base add-ons) */
  slug?: string;
  name: string;
  nameKey: string;
  tagline: string;
  taglineKey: string;
  /** true = price comes from useCurrency(); false/undefined = free */
  isPaid?: boolean;
  features: AddonFeature[];
  isBundle?: boolean;
  isBase?: boolean;
}

const ADDONS: Addon[] = [
  {
    id: 'free',
    slug: 'free',
    Icon: PenLine,
    name: 'Grátis',
    nameKey: 'addonsPricing.freeName',
    tagline: 'Escreva seu livro sem pagar nada',
    taglineKey: 'addonsPricing.freeTagline',
    isBase: true,
    features: [
      { text: 'Editor completo', key: 'addonsPricing.freeFeatureEditor' },
      { text: 'Dark mode', key: 'addonsPricing.freeFeatureDarkMode' },
      { text: 'Export PDF e DOCX', key: 'addonsPricing.freeFeatureExport' },
      { text: '30 ações de IA/mês (texto)', key: 'addonsPricing.freeFeatureAiActions' },
      { text: '1 geração de capa/mês', key: 'addonsPricing.freeFeatureCoverGen' },
    ],
  },
  {
    id: 'ia-studio',
    slug: 'ia-studio',
    name: 'IA Studio',
    nameKey: 'addonsPricing.iaStudioName',
    tagline: 'Escrita assistida por IA com cota generosa',
    taglineKey: 'addonsPricing.iaStudioTagline',
    isPaid: true,
    features: [
      { text: 'Reescrever, mudar tom, expandir', key: 'addonsPricing.iaStudioFeatureRewrite', highlight: true },
      { text: 'RAG sobre o seu livro', key: 'addonsPricing.iaStudioFeatureRag', highlight: true },
      { text: 'IA de escrita (≈1.500 ações/mês)', key: 'addonsPricing.iaStudioFeatureActions', highlight: true },
      { text: '10 gerações de capa/mês', key: 'addonsPricing.iaStudioFeatureCoverGen', highlight: true },
    ],
  },
  {
    id: 'craft',
    slug: 'craft',
    name: 'Craft',
    nameKey: 'addonsPricing.craftName',
    tagline: 'Ferramentas de disciplina e formato para escritores sérios',
    taglineKey: 'addonsPricing.craftTagline',
    isPaid: true,
    features: [
      { text: 'Histórico de versões', key: 'addonsPricing.craftFeatureVersionHistory', highlight: true },
      { text: 'Stats avançadas', key: 'addonsPricing.craftFeatureStats', highlight: true },
      { text: 'Metas personalizadas', key: 'addonsPricing.craftFeatureGoals' },
      { text: 'Check-ins diários', key: 'addonsPricing.craftFeatureCheckins' },
      { text: 'Export EPUB e HTML', key: 'addonsPricing.craftFeatureExport', highlight: true },
    ],
  },
  {
    id: 'outlining',
    slug: 'outlining',
    name: 'Outlining',
    nameKey: 'addonsPricing.outliningName',
    tagline: 'Organize universos, personagens e tramas',
    taglineKey: 'addonsPricing.outliningTagline',
    isPaid: true,
    features: [
      { text: 'Boards ilimitados', key: 'addonsPricing.outliningFeatureBoards', highlight: true },
      { text: 'Notas ilimitadas com atributos', key: 'addonsPricing.outliningFeatureNotes', highlight: true },
      { text: 'Templates (personagens, locais…)', key: 'addonsPricing.outliningFeatureTemplates', highlight: true },
      { text: 'Dispositivos narrativos', key: 'addonsPricing.outliningFeatureDevices' },
      { text: 'Notas fixadas', key: 'addonsPricing.outliningFeaturePinnedNotes' },
    ],
  },
  {
    id: 'bundle',
    slug: 'bundle',
    name: 'Bundle "Tudo"',
    nameKey: 'addonsPricing.bundleName',
    tagline: 'Todos os add-ons juntos — melhor valor',
    taglineKey: 'addonsPricing.bundleTagline',
    isPaid: true,
    isBundle: true,
    features: [
      { text: 'IA Studio completo', key: 'addonsPricing.bundleFeatureIaStudio', highlight: true },
      { text: 'Craft completo', key: 'addonsPricing.bundleFeatureCraft', highlight: true },
      { text: 'Outlining completo', key: 'addonsPricing.bundleFeatureOutlining', highlight: true },
      { text: '~20% de desconto vs. avulso', key: 'addonsPricing.bundleFeatureDiscount', highlight: true },
    ],
  },
];

// ---------------------------------------------------------------------------
// Icon size map — per-slug overrides so each illustration reads at the right scale
// ---------------------------------------------------------------------------

const ICON_SIZE: Record<string, string> = {
  'free': 'h-40 w-40',
  'ia-studio': 'h-40 w-40',
  'craft': 'h-[11.5rem] w-[11.5rem]',
  'outlining': 'h-[9.5rem] w-[9.5rem]',
  'bundle': 'h-40 w-40',
};

// Compact variant (account view / upgrade modal): same per-slug PROPORTIONS as
// the landing, scaled down, so each illustration reads at the right size
// instead of all being forced to one square (which made them look uneven).
const ICON_SIZE_COMPACT: Record<string, string> = {
  // +30% over the previous compact sizes (icons read too small in the modal).
  'free': 'h-[11.38rem] w-[11.38rem]',
  'ia-studio': 'h-[11.38rem] w-[11.38rem]',
  'craft': 'h-[13.4rem] w-[13.4rem]',
  'outlining': 'h-[10.36rem] w-[10.36rem]',
  'bundle': 'h-[11.38rem] w-[11.38rem]',
};

// ---------------------------------------------------------------------------
// Toggle component
// ---------------------------------------------------------------------------

function BillingToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center p-1 rounded-full border border-border bg-card shadow-sm">
      <button
        onClick={() => onChange('monthly')}
        className={cn(
          'px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 md:px-5 md:py-2 md:text-sm',
          cycle === 'monthly'
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {t('addonsPricing.billingMonthly', 'Mensal')}
      </button>
      <button
        onClick={() => onChange('yearly')}
        className={cn(
          'flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 md:gap-2 md:px-5 md:py-2 md:text-sm',
          cycle === 'yearly'
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        {t('addonsPricing.billingYearly', 'Anual')}
        <span
          className={cn(
            'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full transition-colors md:px-2',
            cycle === 'yearly'
              ? 'bg-accent/20 text-accent-foreground'
              : 'bg-accent/10 text-accent'
          )}
        >
          {t('addonsPricing.billingYearlyDiscount', '~17% off')}
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single addon card
// ---------------------------------------------------------------------------

interface AddonCardProps {
  addon: Addon;
  cycle: BillingCycle;
  compact?: boolean;
  /** Stagger index for the entrance animation. */
  index?: number;
  onSubscribe: (id: AddonId, cycle: BillingCycle) => void;
  currency: 'USD' | 'BRL';
  fmt: (amount: number) => string;
  monthly: (id: string) => number;
  yearlyMonthly: (id: string) => number;
  yearly: (id: string) => number;
}

function AddonCard({
  addon,
  cycle,
  compact,
  index = 0,
  onSubscribe,
  currency,
  fmt,
  monthly,
  yearlyMonthly,
  yearly,
}: AddonCardProps) {
  const { t } = useTranslation();
  const { Icon, slug } = addon;

  // Resolved display price (per month)
  const displayPrice = addon.isPaid
    ? cycle === 'yearly'
      ? yearlyMonthly(addon.id)
      : monthly(addon.id)
    : null;

  const yearlyTotal = addon.isPaid ? yearly(addon.id) : null;

  // Currency symbol for inline display
  const symbol = currency === 'BRL' ? 'R$' : '$';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.045, type: 'spring', stiffness: 220, damping: 28, mass: 0.6 }}
      className={cn(
        'group relative flex h-full flex-col rounded-2xl border transition-all duration-300',
        compact ? 'p-4 pt-[7rem]' : 'p-5 md:p-6 pt-[5.5rem]',
        addon.isBundle
          ? 'border-accent/60 bg-accent/5 shadow-lg shadow-accent/10 ring-1 ring-accent/30'
          : addon.isBase
          ? 'border-border bg-card/60'
          : 'border-border bg-card hover:border-primary/40 hover:shadow-md'
      )}
    >
      {/* Bundle badge — top-RIGHT (o ícone grande do bundle mora no topo-esquerdo,
          então centralizar fazia o ícone cobrir a badge). z-10 fica acima do ícone. */}
      {addon.isBundle && (
        <div className="absolute -top-3.5 right-3 z-10 whitespace-nowrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest bg-accent text-accent-foreground shadow-md">
            <Package size={10} className="fill-current" />
            {t('addonsPricing.bestValueBadge', 'Melhor Valor')}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-3 mt-1">
        {/* Icon: illustrated webp for paid add-ons, bleeds above the card; lucide icon for free base */}
        {slug ? (
          <div
            className={cn(
              'pointer-events-none absolute -left-3 md:-left-2 top-0 -translate-y-1/2 transition-transform duration-300 group-hover:scale-105',
              compact
                ? (ICON_SIZE_COMPACT[slug] ?? 'h-28 w-28')
                : (ICON_SIZE[slug] ?? 'h-40 w-40')
            )}
          >
            <img
              src={`/addons/${slug}.webp`}
              alt={t(addon.nameKey, addon.name)}
              className="h-full w-full object-contain drop-shadow-xl"
            />
            {/* Subtle ambient dust floating over the illustration */}
            <AmbientParticles count={7} className="mix-blend-screen" />
          </div>
        ) : (
          <div
            className={cn(
              'flex items-center justify-center rounded-lg shrink-0',
              compact ? 'w-8 h-8 p-1.5' : 'w-9 h-9 p-2',
              addon.isBase ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
            )}
          >
            {Icon && <Icon size={compact ? 16 : 18} strokeWidth={1.75} />}
          </div>
        )}
        <div className="min-w-0">
          <h3
            className={cn(
              'font-serif font-bold leading-tight',
              compact ? 'text-sm' : 'text-base md:text-lg',
              addon.isBundle ? 'text-accent' : 'text-primary'
            )}
          >
            {t(addon.nameKey, addon.name)}
          </h3>
          {!compact && (
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
              {t(addon.taglineKey, addon.tagline)}
            </p>
          )}
        </div>
      </div>

      {/* Price */}
      <div className="mb-1">
        <AnimatePresence mode="wait">
          {!addon.isPaid ? (
            <motion.span
              key="free"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={cn(
                'font-extrabold tracking-tight text-foreground',
                compact ? 'text-xl' : 'text-2xl md:text-3xl'
              )}
            >
              {t('addonsPricing.priceFree', 'Grátis')}
            </motion.span>
          ) : (
            <motion.div
              key={`${cycle}-${addon.id}-${currency}`}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.2 }}
              className="flex items-baseline gap-1"
            >
              <span className="text-xs text-muted-foreground">{symbol}</span>
              <span
                className={cn(
                  'font-extrabold tracking-tight',
                  compact ? 'text-xl' : 'text-2xl md:text-3xl',
                  addon.isBundle ? 'text-accent' : 'text-foreground'
                )}
              >
                {displayPrice !== null
                  ? new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(displayPrice)
                  : '—'}
              </span>
              <span className="text-xs text-muted-foreground">{t('addonsPricing.perMonth', '/mês')}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Yearly billing note */}
      {cycle === 'yearly' && addon.isPaid && yearlyTotal !== null && (
        <p className="text-[11px] text-muted-foreground mb-3">
          {t('addonsPricing.yearlyBillingNote', 'Cobrado {{amount}}/ano — 2 meses grátis', {
            amount: fmt(yearlyTotal),
          })}
        </p>
      )}
      {!(cycle === 'yearly' && addon.isPaid) && <div className="mb-3" />}

      {/* Divider */}
      <div className="border-t border-border mb-3" />

      {/* Features */}
      <ul className={cn('flex-1 space-y-2 mb-4', compact && 'space-y-1.5')}>
        {addon.features.map((feat, i) => (
          <li key={i} className="flex items-start gap-2 text-xs">
            <Check
              size={11}
              className={cn(
                'shrink-0 mt-0.5',
                feat.highlight ? 'text-primary' : 'text-accent'
              )}
            />
            <span
              className={cn(
                feat.highlight
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground'
              )}
            >
              {t(feat.key, feat.text)}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {!addon.isPaid ? (
        <button
          disabled
          className="w-full py-2.5 rounded-xl text-[11px] font-bold border border-border/50 bg-muted/30 text-muted-foreground cursor-default font-mono uppercase tracking-wide"
        >
          {t('addonsPricing.currentPlanButton', 'Plano Atual')}
        </button>
      ) : (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onSubscribe(addon.id, cycle)}
          className={cn(
            'w-full py-2.5 rounded-xl text-[11px] font-bold border font-mono uppercase tracking-wide transition-all duration-200 flex items-center justify-center gap-1.5',
            addon.isBundle
              ? 'bg-accent text-accent-foreground border-accent hover:opacity-90 shadow-md shadow-accent/20'
              : 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
          )}
        >
          <Plus size={11} />
          {t('addonsPricing.addButton', 'Adicionar')}
        </motion.button>
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Cover credits top-up block
// ---------------------------------------------------------------------------

function CoverCreditsBlock({
  onBuyCredits,
  fmt,
  coverCredits,
}: {
  onBuyCredits: () => void;
  fmt: (amount: number) => string;
  coverCredits: number;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.15 }}
      className="mt-8 rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <ImagePlus size={18} className="text-primary" />
        </div>
        <div className="min-w-0">
          <p className="font-serif font-bold text-sm text-foreground leading-tight">
            {t('addonsPricing.coverCreditsTitle', 'Precisa de mais capas?')}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t('addonsPricing.coverCreditsDesc', 'Créditos avulsos — {{amount}} por 10 gerações de capa', {
              amount: fmt(coverCredits),
            })}
          </p>
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        onClick={onBuyCredits}
        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-[11px] font-bold font-mono uppercase tracking-wide border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
      >
        <Plus size={11} />
        {t('addonsPricing.buyCreditsButton', 'Comprar Créditos')}
      </motion.button>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Main exported component
// ---------------------------------------------------------------------------

export function AddonsPricing({
  variant = 'public',
  onSubscribe: externalOnSubscribe,
  forceSlider = false,
}: AddonsPricingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  // Default to yearly — it's the best-value tab, so the modal should always
  // open with it selected (and the priciest/top plan highlighted below).
  const [cycle, setCycle] = useState<BillingCycle>('yearly');
  const { currency, fmt, monthly, yearly, yearlyMonthly, coverCredits } = useCurrency();

  // Mobile slider for add-on cards (non-bundle)
  const sliderRef = useRef<HTMLDivElement>(null);
  const [mobileIndex, setMobileIndex] = useState(0);

  const nonBaseAddons = ADDONS.filter((a) => !a.isBase && !a.isBundle);
  const bundle = ADDONS.find((a) => a.isBundle)!;
  const base = ADDONS.find((a) => a.isBase)!;

  const handleScroll = () => {
    if (!sliderRef.current) return;
    const el = sliderRef.current;
    // base + add-ons (+ bundle when forced into the slider)
    const itemCount = nonBaseAddons.length + (forceSlider ? 2 : 1);
    const itemWidth = el.scrollWidth / itemCount;
    const idx = Math.round(el.scrollLeft / itemWidth);
    setMobileIndex(Math.min(Math.max(idx, 0), itemCount - 1));
  };

  // Asaas needs a CPF/CNPJ to issue any charge; we collect it once and reuse it.
  const [cpfModal, setCpfModal] = useState<
    null | { kind: 'addon'; addonId: AddonId; cycle: BillingCycle } | { kind: 'credits' }
  >(null);
  const [cpfInput, setCpfInput] = useState('');
  const [cpfBusy, setCpfBusy] = useState(false);

  const storedCpf = () => localStorage.getItem('reformulat_cpf') || undefined;
  const isCpfError = (e: unknown) =>
    !!e && typeof e === 'object' && (('status' in e && (e as { status?: number }).status === 422) ||
      ('message' in e && (e as { message?: string }).message === 'CPF_REQUIRED'));
  const checkoutError = () =>
    notify.error(
      t('addonsPricing.checkoutErrorTitle', 'Ops'),
      t('addonsPricing.checkoutErrorDesc', 'Não foi possível abrir o pagamento. Tente novamente.'),
    );

  // Not logged in → send them to sign up first (they can't be charged otherwise).
  const requireLogin = (): boolean => {
    if (authUser) return true;
    notify.info(
      t('addonsPricing.loginToSubscribeTitle', 'Crie sua conta'),
      t('addonsPricing.loginToSubscribeDesc', 'Faça login ou crie sua conta para assinar — você começa com 7 dias grátis.'),
    );
    navigate('/app');
    return false;
  };

  // Returns true if the checkout was opened; throws (for CPF handling) on failure.
  const openAddonCheckout = async (addonId: AddonId, billingCycle: BillingCycle, cpf?: string) => {
    const res = await api.activateAddon(addonId, billingCycle === 'yearly' ? 'annual' : 'monthly', cpf);
    if (res.paymentUrl) { window.location.href = res.paymentUrl; return true; }
    return false;
  };
  const openCreditsCheckout = async (cpf?: string) => {
    const res = await api.buyCoverCredits(1, cpf); // one pack (10 credits)
    if (res.paymentUrl) { window.location.href = res.paymentUrl; return true; }
    return false;
  };

  const handleSubscribe = async (addonId: AddonId, billingCycle: BillingCycle) => {
    if (externalOnSubscribe) { externalOnSubscribe(addonId, billingCycle); return; }
    if (addonId === 'free') return;
    if (!requireLogin()) return;
    try {
      if (!(await openAddonCheckout(addonId, billingCycle, storedCpf()))) checkoutError();
    } catch (e) {
      if (isCpfError(e)) { setCpfInput(storedCpf() || ''); setCpfModal({ kind: 'addon', addonId, cycle: billingCycle }); }
      else checkoutError();
    }
  };

  const handleBuyCoverCredits = async () => {
    if (!requireLogin()) return;
    try {
      if (!(await openCreditsCheckout(storedCpf()))) checkoutError();
    } catch (e) {
      if (isCpfError(e)) { setCpfInput(storedCpf() || ''); setCpfModal({ kind: 'credits' }); }
      else checkoutError();
    }
  };

  const submitCpf = async () => {
    const cpf = cpfInput.replace(/\D/g, '');
    if (cpf.length !== 11 && cpf.length !== 14) {
      notify.error(t('addonsPricing.cpfInvalidTitle', 'CPF/CNPJ inválido'), t('addonsPricing.cpfInvalidDesc', 'Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.'));
      return;
    }
    localStorage.setItem('reformulat_cpf', cpf);
    const target = cpfModal;
    setCpfBusy(true);
    try {
      const ok = target?.kind === 'addon'
        ? await openAddonCheckout(target.addonId, target.cycle, cpf)
        : await openCreditsCheckout(cpf);
      if (!ok) checkoutError();
    } catch { checkoutError(); }
    finally { setCpfBusy(false); setCpfModal(null); }
  };

  const compact = variant === 'app';

  // Cards for the slider. Normally: base + individual add-ons (bundle is shown
  // full-width below). When forced into a carousel (Upgrade modal), the bundle
  // joins the carousel so everything is swipeable in one row.
  const sliderCards = forceSlider ? [base, ...nonBaseAddons, bundle] : [base, ...nonBaseAddons];

  const cardProps = { cycle, compact, onSubscribe: handleSubscribe, currency, fmt, monthly, yearly, yearlyMonthly };

  return (
    <div className={cn('w-full', compact ? 'space-y-3' : 'space-y-8')}>
      {/* Toggles row — pr clears the modal's floating close (X) button on mobile */}
      <div className={cn('flex flex-col items-center gap-3', compact && 'pr-10 md:pr-0')}>
        <BillingToggle cycle={cycle} onChange={setCycle} />
        <AnimatePresence>
          {cycle === 'yearly' && (
            <motion.p
              key="savings-note"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="text-[11px] text-accent font-medium"
            >
              {t('addonsPricing.yearlySavingsNote', 'Economize ~17% — equivale a 2 meses grátis')}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Stackable hint */}
      {!compact && (
        <p className="text-center text-xs text-muted-foreground">
          <Sparkles size={11} className="inline mr-1 text-accent" />
          {t('addonsPricing.stackableHint', 'Combine os add-ons que quiser — cada um é independente e empilhável.')}
        </p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Mobile: horizontal slider (base + individual add-ons)               */}
      {/* Desktop: grid                                                         */}
      {/* ------------------------------------------------------------------ */}
      <div>
        {/* Slider row */}
        <div
          ref={sliderRef}
          onScroll={handleScroll}
          className={cn(
            // Mobile: snap scroll
            'flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2',
            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // pt-28 matches the card's own pt-[7rem] so the bigger (compact)
            // illustrations have exactly enough room to bleed above the card
            // without clipping or crowding the tabs above.
            compact
              ? '-mx-5 px-5 pt-28 lg:mx-0 lg:px-0'
              : '-mx-4 px-4 pt-20 lg:mx-0 lg:px-0',
            // Desktop: grid — unless forced to stay a carousel at every breakpoint
            !forceSlider && 'lg:grid lg:grid-cols-4 lg:gap-5 lg:overflow-visible lg:pb-0',
            'items-stretch'
          )}
        >
          {sliderCards.map((addon, i) => (
            <div
              key={addon.id}
              className={cn(
                // Mobile snap card sizing
                'snap-center shrink-0',
                compact ? 'w-[85%] sm:w-[48%]' : 'w-[82%] sm:w-[46%]',
                // Desktop: full width in grid (unless kept as a carousel)
                !forceSlider && 'lg:w-auto lg:shrink'
              )}
            >
              <AddonCard addon={addon} index={i} {...cardProps} />
            </div>
          ))}
        </div>

        {/* Dot indicators — mobile only, or always when forced into a carousel */}
        <div
          className={cn('flex justify-center gap-1.5 mt-3', !forceSlider && 'lg:hidden')}
          role="tablist"
          aria-label={t('addonsPricing.tabsAriaLabel', 'Add-ons')}
        >
          {sliderCards.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === mobileIndex}
              aria-label={t('addonsPricing.addonTabAriaLabel', 'Add-on {{number}}', { number: i + 1 })}
              onClick={() => {
                if (!sliderRef.current) return;
                const itemWidth =
                  sliderRef.current.scrollWidth / sliderCards.length;
                sliderRef.current.scrollTo({
                  left: itemWidth * i,
                  behavior: 'smooth',
                });
              }}
              className={cn(
                'h-1.5 rounded-full transition-all duration-300',
                i === mobileIndex ? 'w-6 bg-primary' : 'w-1.5 bg-border'
              )}
            />
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Bundle card — full width below the grid. When forced into a         */}
      {/* carousel the bundle already lives in the slider, so skip it here.   */}
      {/* ------------------------------------------------------------------ */}
      {!forceSlider && (
        <div className={cn(bundle.isBundle && 'mt-20')}>
          <AddonCard addon={bundle} {...cardProps} />
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Cover credits top-up — hidden in the compact carousel (upgrade      */}
      {/* modal) to keep it short and avoid an accidental one-tap purchase.   */}
      {/* ------------------------------------------------------------------ */}
      {!forceSlider && (
        <CoverCreditsBlock onBuyCredits={handleBuyCoverCredits} fmt={fmt} coverCredits={coverCredits} />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Trust strip (public variant only)                                   */}
      {/* ------------------------------------------------------------------ */}
      {variant === 'public' && (
        <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6 text-xs text-muted-foreground pt-2">
          <span className="flex items-center gap-2">
            <Shield size={13} className="text-accent" />
            {t('addonsPricing.trustSecurePayment', 'Pagamento seguro via Asaas')}
          </span>
          <span className="flex items-center gap-2">
            <RotateCcw size={13} className="text-accent" />
            {t('addonsPricing.trustGuarantee', 'Garantia de 7 dias')}
          </span>
          <span className="flex items-center gap-2">
            <Check size={13} className="text-accent" />
            {t('addonsPricing.trustCancelAnytime', 'Cancele quando quiser')}
          </span>
          <span className="flex items-center gap-2">
            <Zap size={13} className="text-accent" />
            {t('addonsPricing.trustStackable', 'Add-ons empilháveis e independentes')}
          </span>
        </div>
      )}

      {/* CPF/CNPJ modal — Asaas requires it to issue the charge. */}
      {cpfModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
          onClick={() => !cpfBusy && setCpfModal(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-card border border-border p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-lg font-bold text-foreground mb-1">
              {t('addonsPricing.cpfModalTitle', 'Só falta seu CPF')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('addonsPricing.cpfModalDesc', 'A Asaas exige um CPF ou CNPJ para emitir a cobrança (Pix, boleto ou cartão).')}
            </p>
            <input
              autoFocus
              inputMode="numeric"
              value={cpfInput}
              onChange={(e) => setCpfInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !cpfBusy) submitCpf(); }}
              placeholder={t('addonsPricing.cpfPlaceholder', 'CPF ou CNPJ (só números)')}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none focus:border-accent"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => setCpfModal(null)}
                disabled={cpfBusy}
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                {t('addonsPricing.cpfCancel', 'Cancelar')}
              </button>
              <button
                onClick={submitCpf}
                disabled={cpfBusy}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              >
                {cpfBusy ? t('addonsPricing.cpfBusy', 'Abrindo…') : t('addonsPricing.cpfContinue', 'Ir para o pagamento')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
